import type { RumEvent } from "@datadog/browser-rum";

const REDACTED = "[REDACTED]";

const AUTHORIZATION_VALUE =
  /(authorization\\?["']?\s*[:=]\s*\\?["']?)[^"'\\\n\r,}]*/gi;
const AUTHORIZATION_KEY = /^(?:proxy-)?authorization$/i;
const BEARER_TOKEN = /Bearer(?:\s|%20|\+)+[A-Za-z0-9._~+/=-]+/gi;

function redactString(value: string): string {
  return value
    .replace(AUTHORIZATION_VALUE, `$1${REDACTED}`)
    .replace(BEARER_TOKEN, `Bearer ${REDACTED}`);
}

function redactValue(value: unknown): unknown {
  if (typeof value === "string") return redactString(value);
  if (typeof value !== "object" || value === null) return value;
  if (Array.isArray(value)) return value.map(redactValue);
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      AUTHORIZATION_KEY.test(key) && item != null
        ? REDACTED
        : redactValue(item),
    ])
  );
}

function redactFields<T extends object>(
  target: T | undefined,
  keys: (keyof T)[]
) {
  if (!target) return;
  for (const key of keys) {
    const value = target[key];
    if (typeof value === "string") {
      target[key] = redactString(value) as T[keyof T];
    }
  }
}

function sanitizeEvent(event: RumEvent): true {
  if (event.context) {
    event.context = redactValue(event.context) as typeof event.context;
  }
  redactFields(event.view, ["url", "referrer", "name"]);

  if (event.type === "error") {
    redactFields(event.error, ["message", "stack", "fingerprint"]);
    redactFields(event.error.resource, ["url"]);
  }
  if (event.type === "resource") {
    redactFields(event.resource, ["url"]);
    redactFields(event.resource.graphql, ["variables"]);
  }
  if (event.type === "action") {
    redactFields(event.action.target, ["name"]);
  }
  if (event.type === "view") {
    redactFields(event.view.performance?.lcp, ["resource_url"]);
  }
  if (event.type === "long_task") {
    for (const script of event.long_task.scripts ?? []) {
      redactFields(script, ["source_url", "invoker"]);
    }
  }

  return true;
}

export { sanitizeEvent };
